import { Component, OnInit, OnDestroy, inject, input, output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { Subject, takeUntil } from 'rxjs';
import { ContactFormService } from '../../services/contact-form.service';
import { ContactWithRole, ContactFormData, ContactFormConfig } from '../../interfaces/contact.interface';

@Component({
    selector: 'qc-contact-form',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, InputTextModule, ButtonModule],
    template: `
        <form [formGroup]="form" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="form-field">
                    <label class="block text-sm mb-1">Full Name *</label>
                    <input
                        pInputText
                        formControlName="fullName"
                        placeholder="Enter full name"
                        class="w-full"
                        [class.border-rose-500]="hasFieldError('fullName')"
                        [readonly]="readonly()"
                    />
                    <div *ngIf="getFieldError('fullName')" class="text-rose-500 text-sm mt-1">
                        {{ getFieldError('fullName') }}
                    </div>
                </div>

                <div class="form-field">
                    <label class="block text-sm mb-1">Email *</label>
                    <input
                        pInputText
                        type="email"
                        formControlName="email"
                        placeholder="email@domain.com"
                        class="w-full"
                        [class.border-rose-500]="hasFieldError('email')"
                        [readonly]="readonly()"
                    />
                    <div *ngIf="getFieldError('email')" class="text-rose-500 text-sm mt-1">
                        {{ getFieldError('email') }}
                    </div>
                </div>

                <div class="form-field">
                    <label class="block text-sm mb-1">Position Title</label>
                    <input
                        pInputText
                        formControlName="positionTitle"
                        placeholder="Job title"
                        class="w-full"
                        [readonly]="readonly()"
                    />
                </div>

                <div class="form-field">
                    <label class="block text-sm mb-1">Phone</label>
                    <input
                        pInputText
                        formControlName="phone"
                        placeholder="+1 (555) 123-4567"
                        class="w-full"
                        [readonly]="readonly()"
                    />
                </div>

                <div *ngIf="showContextField()" class="form-field">
                    <label class="block text-sm mb-1">Context</label>
                    <input
                        pInputText
                        formControlName="context"
                        placeholder="e.g., Emergency Contact, Brand Manager"
                        class="w-full"
                        [readonly]="readonly()"
                    />
                </div>

                <div *ngIf="showRoleFields()" class="form-field">
                    <label class="block text-sm mb-1">Role Description</label>
                    <input
                        pInputText
                        formControlName="roleDescription"
                        placeholder="Role in organization"
                        class="w-full"
                        [readonly]="readonly()"
                    />
                </div>
            </div>

            <div *ngIf="!readonly() && showButtons()" class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <p-button
                    type="button"
                    severity="secondary"
                    variant="outlined"
                    (click)="cancel.emit()"
                    label="Cancel"
                    size="small"
                />
                <p-button
                    type="button"
                    severity="primary"
                    (click)="handleSave()"
                    [label]="isEditMode() ? 'Save Changes' : 'Save Contact'"
                    size="small"
                    [disabled]="!form.valid || saving()"
                />
            </div>
        </form>
    `,
})
export class ContactFormComponent implements OnInit, OnDestroy {
    private contactFormService = inject(ContactFormService);
    private destroy$ = new Subject<void>();

    contact = input<ContactWithRole | null>(null);
    config = input<Partial<ContactFormConfig>>({});
    readonly = input<boolean>(false);
    showButtons = input<boolean>(true);
    deferSave = input<boolean>(false);

    save = output<ContactWithRole>();
    cancel = output<void>();
    validityChange = output<boolean>();

    form!: FormGroup;
    saving = signal<boolean>(false);

    isEditMode = computed(() => !!this.contact()?.id);
    showContextField = computed(() => this.finalConfig().showContextField);
    showRoleFields = computed(() => this.finalConfig().showRoleFields);

    private finalConfig = computed(() => ({
        showRoleFields: true,
        showContextField: true,
        requiredFields: ['fullName'],
        maxLengths: {
            fullName: 255,
            positionTitle: 255,
            email: 255,
            phone: 50,
            context: 255,
            roleDescription: 255,
        },
        ...this.config(),
    }));

    ngOnInit(): void {
        this.initializeForm();
        this.setupFormWatchers();
        this.populateFormData();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initializeForm(): void {
        this.form = this.contactFormService.createContactForm(this.config());
    }

    private setupFormWatchers(): void {
        this.form.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.validityChange.emit(this.form.valid);
        });
    }

    private populateFormData(): void {
        const contact = this.contact();
        if (contact) {
            const formData = this.contactFormService.convertContactToFormData(contact);
            this.form.patchValue(formData);
        }
    }

    hasFieldError(fieldName: string): boolean {
        return this.contactFormService.hasFieldError(this.form, fieldName);
    }

    getFieldError(fieldName: string): string | null {
        return this.contactFormService.getFieldError(this.form, fieldName);
    }

    handleSave(): void {
        if (!this.form.valid || this.saving()) {
            return;
        }

        const formData: ContactFormData = this.form.value;

        if (this.deferSave()) {
            const isEdit = typeof formData.id === 'string' && formData.id.trim().length > 0;
            const tempId = `temp-${Date.now()}-${Math.random()}`;
            const contactId = isEdit ? formData.id! : tempId;

            const contactData: any = {
                id: contactId,
                fullName: formData.fullName,
                positionTitle: formData.positionTitle || '',
                email: formData.email || '',
                phone: formData.phone || '',
                context: formData.context || '',
                roleDescription: formData.roleDescription || '',
                createdAt: new Date(),
                updatedAt: new Date(),
            };

            if (!isEdit) {
                contactData._isNew = true;
            } else {
                contactData._isUpdated = true;
            }

            this.save.emit(contactData as ContactWithRole);
            return;
        }

        this.saving.set(true);

        const cleanFormData = {
            fullName: formData.fullName,
            positionTitle: formData.positionTitle,
            email: formData.email,
            phone: formData.phone,
        };

        const operation = this.isEditMode()
            ? this.contactFormService.updateContact(formData.id!, cleanFormData)
            : this.contactFormService.createContact(cleanFormData);

        operation.pipe(takeUntil(this.destroy$)).subscribe({
            next: (result) => {
                this.saving.set(false);
                if (result.success && result.contact) {
                    this.save.emit(result.contact);
                } else {
                }
            },
            error: (error) => {
                this.saving.set(false);
            },
        });
    }
}
